import React, { useCallback, useContext, useEffect } from 'react'

import {
  useFormDataV2,
  useFormValidation,
  validateIsNotBlankWithLocale,
  validatePhoneWithLocale,
} from '@monorepo/utils'
import PhoneInput from './PhoneInput'
import { RegField } from './types'
import { ApiCore, mapLanguageToApi, useRegister } from '@monorepo/api'
import { ContextApp } from '@monorepo/service'
import { parsePhoneNumber } from 'libphonenumber-js'
import RegisterButton from '../RegisterButton'
import { getRegistrationBonus } from '../../constants'
import { UserAlreadyExists } from '@monorepo/uikit'
import { useFormGeo } from '../../hooks/useFormGeo'
import { type SupportedGeo } from '../../geo/config'
import { useGeoRuntime } from '../../geo/useGeoRuntime.ts'
import { FormCurrency } from '../FormCurrency/index.tsx'

declare const window: Window &
  typeof globalThis & {
    updateContent?: any
  }

const VALIDATORS = {
  rules: {
    clean_phone: [validateIsNotBlankWithLocale, validatePhoneWithLocale],
  },
  defaultData: {
    clean_phone: '',
  },
}

const FormPhone: React.FC = () => {
  const { sid, setDataRegister, isCdaBalancerInfoLoading, cdaBalancerInfo } = useContext(ContextApp)

  const { syncGeoState } = useGeoRuntime()

  // Используем хук для локального гео формы
  const { formGeo, formGeoConfig, handleFormGeoChange } = useFormGeo()

  const bonus = getRegistrationBonus(formGeo)

  const { handleRequest, fieldErrorMsg, commonErrorMsg, isLoading } = useRegister({
    sid,
    bonusId: bonus?.bonusId,
    setDataRegister,
  })

  const phoneErrorMessage = fieldErrorMsg ? fieldErrorMsg['clean_phone'] : null

  const { validateForm } = useFormValidation(VALIDATORS.rules)
  const { form, onValidate, setForm } = useFormDataV2({
    validateForm,
    defaultData: VALIDATORS.defaultData,
  })

  const handlePhoneChange = useCallback(
    (key: keyof RegField, value: RegField[typeof key]) => {
      setForm((form) => ({
        ...form,
        data: {
          ...form.data,
          [key]: value,
        },
      }))
    },
    [setForm],
  )

  const handlePhoneGeoChange = useCallback(
    (nextGeo: SupportedGeo) => {
      handleFormGeoChange(nextGeo)
      setForm((form) => ({
        ...form,
        data: {
          ...form.data,
          clean_phone: '',
        },
        statuses: {
          ...form.statuses,
          clean_phone: undefined,
        },
        errors: {
          ...form.errors,
          clean_phone: '',
        },
      }))
    },
    [handleFormGeoChange, setForm, formGeo],
  )

  const handleSubmit = useCallback(() => {
    onValidate('clean_phone')((data) => {
      const { nationalNumber, countryCallingCode } = parsePhoneNumber(data.clean_phone)
      const nextGeoState = syncGeoState(formGeo)

      const payload = {
        account_registered_by: ApiCore.PublicAccountRegistrationTypeEnum.PhoneNumber,
        language: mapLanguageToApi(nextGeoState.language),
        country_of_residence: formGeoConfig.countryOfResidence,
        currency: formGeoConfig.currency,
        privacy_policy: true,
        terms_and_conditions: true,
        receive_promos_enabled: true,
        clean_phone: Number(nationalNumber),
        phone_country_code: `+${countryCallingCode}`,
      }

      console.log('Полный payload:', payload)

      handleRequest(payload)
    })
  }, [formGeo, formGeoConfig, handleRequest, onValidate, syncGeoState])

  const fields = ['clean_phone']
  const touched = fields.some((field) => form.statuses[field as keyof typeof form.statuses])
  const isFormValid = fields.every(
    (field) => form.statuses[field as keyof typeof form.statuses] === 'valid',
  )

  useEffect(() => {
    window.updateContent?.()
    console.log('formGeo', formGeo)
  }, [form.errors])

  return (
    <>
      <div
        className={`ejForm__fields ${touched ? (isFormValid ? 'validForm' : 'invalidForm') : ''}`}
      >
        <PhoneInput
          phoneNumber={form.data.clean_phone}
          onChange={handlePhoneChange}
          onValidate={onValidate}
          onGeoChange={handlePhoneGeoChange}
          selectedGeo={formGeo}
          error={commonErrorMsg ?? phoneErrorMessage ?? form.errors.clean_phone}
        />
        <UserAlreadyExists
          errorMessage={commonErrorMsg || ''}
          loginType={'phone'}
          isBalancerInfoLoading={isCdaBalancerInfoLoading}
          balancerInfo={cdaBalancerInfo}
        />
        <FormCurrency selectedGeo={formGeo} />
      </div>
      <RegisterButton handleSubmit={handleSubmit} disabled={isLoading} />
    </>
  )
}

export default FormPhone
